// Deliberate duplicate of town-square/src/components/arty/ArtyImageBox.tsx (ADR 0002). A change here belongs there too.
// Client divergences: react-i18next; there is no art review surface (it opens in a new tab,
// see `useArtyImageCard`); edit is client's own `ArtworkDetailsForm` dialog, share is client's
// `ShareModal`, and the community ask is the inline `CommunityAskDialog`
// (~/components/arty/community), not town-square's `ArtyCommunityPostModal`. Each surface
// mounts only while it is the open one, so a transcript of cards holds no idle forms.
import { useEffect, useState } from 'react';
import { LayoutGrid, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { imageLoadPhase, showImageFallback, type ImageLoadOutcome } from '@arthelper/shared/image-load-phase';

import ArtworkDetailsForm from '~/components/ArtworkDetailsForm';
import ShareModal from '~/components/ShareModal';
import { VisibilityChip } from '~/components/ImageManager/VisibilityChip';
import { ArtyActionsModal } from '~/components/arty/ArtyActionsModal';
import { useArtyImageCard, type ArtyImageCard } from '~/components/arty/card/useArtyImageCard';
import { CommunityAskDialog } from '~/components/arty/community/CommunityAskDialog';
import { useAnalytics } from '~/context/analytics';
import type { ArtyAction } from '~/lib/arty-actions/catalog';

export type ArtyImageBoxProps = {
  imageId?: string;
  src?: string;
  alt?: string;
};

/**
 * Pieces whose card has been counted as shown, for the page's lifetime. A streamed reply
 * remounts its card when the message is finalised, so a per-mount guard counts it twice:
 * `arty_card_shown` is once per piece per page load.
 */
const shownPieces = new Set<string>();

const PILL =
  'items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[12.5px] font-semibold text-gray-800 transition-colors hover:bg-gray-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-violet-500';

function ShortcutPill({ action, card, wide }: { action: ArtyAction; card: ArtyImageCard; wide?: boolean }) {
  const Icon = action.icon;
  return (
    <button
      className={wide ? `flex w-full justify-center ${PILL}` : `inline-flex ${PILL}`}
      data-action-id={action.id}
      onClick={() => {
        card.run(action, 'card');
      }}
      type="button"
    >
      <Icon aria-hidden="true" className="size-[15px] text-gray-500" />
      {action.label}
    </button>
  );
}

/** Pin 1 full width, pins 2-5 as a wrapped row, then the way into everything else. */
function Shortcuts({ card }: { card: ArtyImageCard }) {
  const { t } = useTranslation('arty-card');
  const [primary, ...rest] = card.shortcuts;
  return (
    <div className="flex flex-col gap-2">
      {primary ? <ShortcutPill action={primary} card={card} wide /> : null}
      {rest.length ? (
        <div className="flex flex-wrap gap-1.5">
          {rest.map((action) => (
            <ShortcutPill action={action} card={card} key={action.id} />
          ))}
        </div>
      ) : null}
      <button
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-gray-200 px-3 py-2 text-[13px] font-semibold text-gray-900 transition-colors hover:bg-gray-300 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
        data-more=""
        onClick={card.openMore}
        type="button"
      >
        <LayoutGrid aria-hidden="true" className="size-4 text-gray-600" />
        {t('card.more')}
      </button>
    </div>
  );
}

function BarePicture({ alt, src, label }: { alt: string; src: string; label: string }) {
  const [outcome, setOutcome] = useState<ImageLoadOutcome>('pending');
  const [seenSrc, setSeenSrc] = useState(src);
  if (src !== seenSrc) {
    setSeenSrc(src);
    setOutcome('pending');
  }
  if (showImageFallback(imageLoadPhase(src, outcome), false)) {
    return (
      <p className="my-4 text-sm text-muted-foreground" data-state="image-unavailable">
        {label}
      </p>
    );
  }
  return (
    <img
      alt={alt}
      className="my-4 max-h-64 max-w-full rounded-md object-contain"
      loading="lazy"
      onError={() => {
        setOutcome('error');
      }}
      src={src}
    />
  );
}

function Thumbnail({ card, src }: { card: ArtyImageCard; src: string }) {
  const { t } = useTranslation('arty-card');
  const [outcome, setOutcome] = useState<ImageLoadOutcome>('pending');
  const [seenSrc, setSeenSrc] = useState(src);
  if (src !== seenSrc) {
    setSeenSrc(src);
    setOutcome('pending');
  }
  const phase = imageLoadPhase(src, outcome);
  const loaded = phase === 'ready';
  if (showImageFallback(phase, card.pending || card.polling)) {
    return (
      <div
        className="flex size-[76px] shrink-0 items-center justify-center rounded-lg bg-gray-100 px-1 text-center text-[10px] leading-tight text-gray-500"
        data-state="image-unavailable"
      >
        {t('card.image-unavailable')}
      </div>
    );
  }
  return (
    <div className="relative size-[76px] shrink-0 overflow-hidden rounded-lg bg-gray-100">
      {!loaded || card.pending ? (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <Loader2 aria-hidden="true" className="size-4 animate-spin text-gray-400" />
        </div>
      ) : null}
      {phase === 'unavailable' ? null : (
        <img
          alt={card.title ?? t('card.artwork-alt')}
          className={`size-full cursor-pointer object-cover transition-opacity duration-300 ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading="lazy"
          onClick={card.openClientArt}
          onError={() => {
            setOutcome('error');
          }}
          onLoad={() => {
            setOutcome('load');
          }}
          src={src}
        />
      )}
      {card.polling ? (
        <div className="absolute bottom-1 right-1 flex items-center gap-1 rounded bg-white/90 px-1.5 py-0.5 shadow-sm">
          <Loader2 aria-hidden="true" className="size-3 animate-spin text-gray-500" />
          <span className="text-[10px] text-gray-600">{t('card.processing')}</span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Everything that opens over the card, one at a time. Exported for `ArtyImageMenuActions`,
 * which opens the same surfaces from an image's "..." menu with no card drawn.
 */
export function ArtyImageSurfaces({ card }: { card: ArtyImageCard }) {
  return (
    <>
      {card.surface === 'more' ? (
        <ArtyActionsModal
          actions={card.actions}
          onClose={card.leave}
          onRun={(action) => {
            card.run(action, 'more');
          }}
          onToggleVisibility={card.showVisibility ? card.toggleVisibility : undefined}
          open
          sparksReady={card.sparksReady}
          summary={card.summary}
          visibilityDisabled={card.visibilityPending}
        />
      ) : null}
      {card.surface === 'community' && card.communityKind ? (
        <CommunityAskDialog
          imageId={card.imageId}
          initialArtwork={card.initialArtwork}
          kind={card.communityKind}
          onClose={card.close}
          onPosted={card.leave}
          open
        />
      ) : null}
      {card.surface === 'share' ? (
        <ShareModal isOpen onClose={card.close} quote={card.shareQuote} title={card.shareTitle} url={card.shareUrl} />
      ) : null}
      {/* My Artwork's own editor, in place, so the artist never leaves the chat. */}
      {card.surface === 'edit' && card.image ? (
        <ArtworkDetailsForm
          contentType="image"
          image={card.image}
          onOpenChange={(next) => {
            if (next) {
              return;
            }
            card.close();
          }}
          open
          type="modal"
        />
      ) : null}
    </>
  );
}

/** An image in the Arty transcript: the action card, or a plain preview when there is no id. */
export function ArtyImageBox({ imageId, src, alt }: ArtyImageBoxProps) {
  const { t } = useTranslation('arty-card');
  const { amplitude } = useAnalytics();
  const card = useArtyImageCard({ imageId, src, alt });
  const shown = card.render === 'card' && Boolean(card.displaySrc) && card.isSignedIn;

  // The denominator the card's action and More events are read against.
  useEffect(() => {
    if (!shown || shownPieces.has(card.imageId)) {
      return;
    }
    shownPieces.add(card.imageId);
    amplitude.track('arty_card_shown', { image_id: card.imageId });
  }, [amplitude, shown, card.imageId]);

  if (card.render === 'none') {
    return null;
  }
  if (card.render === 'bare') {
    return (
      <BarePicture alt={alt ?? t('card.artwork-alt')} src={card.displaySrc ?? ''} label={t('card.image-unavailable')} />
    );
  }
  if (!card.displaySrc) {
    if (card.pending) {
      return (
        <div className="my-4 flex max-w-xs items-center justify-center rounded bg-gray-100 p-6">
          <Loader2 aria-hidden="true" className="size-4 animate-spin text-gray-400" />
        </div>
      );
    }
    return (
      <div className="my-4 max-w-xs rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
        {card.title ?? t('card.artwork-alt')}
      </div>
    );
  }

  return (
    <>
      <div
        className="my-4 w-full max-w-sm overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
        data-arty-image-box=""
      >
        <div className="flex items-start gap-3 p-3">
          <Thumbnail card={card} src={card.displaySrc} />
          <div className="min-w-0 flex-1">
            {card.title ? (
              <h4 className="line-clamp-2 text-sm font-semibold leading-tight text-gray-900">{card.title}</h4>
            ) : null}
            {card.pollExhausted ? (
              <button
                className="mt-1 rounded text-left text-[11px] font-medium text-gray-500 underline underline-offset-2 transition-colors hover:text-gray-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-300"
                data-state="still-processing"
                onClick={card.retry}
                type="button"
              >
                {t('card.still-processing')}
              </button>
            ) : null}
            {card.showVisibility ? (
              <div className="mt-1.5 flex">
                <VisibilityChip
                  disabled={card.visibilityPending}
                  isPublic={card.isPublic}
                  onToggle={card.toggleVisibility}
                />
              </div>
            ) : null}
          </div>
        </div>
        <div className="border-t border-gray-100 px-3 pb-3 pt-2.5">
          {card.isSignedIn ? (
            <Shortcuts card={card} />
          ) : (
            <p className="text-sm text-muted-foreground">{t('card.sign-in')}</p>
          )}
        </div>
      </div>
      {card.isSignedIn ? <ArtyImageSurfaces card={card} /> : null}
    </>
  );
}
